import React, { useState } from "react";
import { useApi } from "../utils/Api";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

export default function ContactPage() {
  const { apiFetch } = useApi();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [subject, setSubject] = useState("Table Reservation");
  const [message, setMessage] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (!fullName || !contactEmail || !contactPhone || !subject || !message) {
      setStatus({
        type: "error",
        message: "Please fill in all required fields.",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await apiFetch("send-contact", {
        name: fullName.trim(),
        email: contactEmail.trim(),
        phone: contactPhone.trim(),
        subject,
        message: message.trim(),
      });

      if (result?.success) {
        navigate("/thank-you");
      } else {
        setStatus({
          type: "error",
          message:
            result?.error ||
            result?.message ||
            "Failed to send message. Please try again.",
        });
      }
    } catch (err) {
      setStatus({
        type: "error",
        message: "Network/server error. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="bg-background-light dark:bg-background-dark font-display text-slate-800 dark:text-slate-100 transition-colors duration-300">
      <Helmet>
        <title>Contact Dee Delicious | Afro-Caribbean Restaurant</title>

        <meta
          name="description"
          content="Contact Dee Delicious Afro-Caribbean Restaurant in Liverpool for reservations, enquiries, or directions. Experience authentic Nigerian and Jamaican cuisine."
        />

        <link rel="canonical" href="https://deedelicious.com/contact" />

        <meta property="og:title" content="Contact Dee Delicious Liverpool" />
        <meta
          property="og:description"
          content="Get in touch with Dee Delicious Afro-Caribbean Restaurant in Liverpool."
        />
        <meta property="og:url" content="https://deedelicious.com/contact" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://deedelicious.com/static/media/logo.06d3da75fe2458d68075.png"
        />

        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {isSubmitting && <SpinnerOverlay />}

      {/* Hero Header */}
      <header className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 tropical-pattern opacity-10"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <span className="inline-block px-4 py-1.5 mb-4 rounded-full bg-primary/10 text-primary font-semibold text-sm tracking-wide uppercase">
            Connect With Us
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 text-secondary dark:text-white">
            We’d Love to <span className="text-primary">Hear From You</span>
          </h1>
        </div>
      </header>

      {/* Contact Section */}
      <section className="max-w-7xl mx-auto px-6 pb-24 mt-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <div className="bg-white dark:bg-zinc-900 p-8 md:p-10 rounded-xl shadow-xl shadow-primary/5 border border-primary/5">
            <h2 className="text-2xl font-bold mb-8 flex items-center gap-3 text-dark-green">
              <span className="material-icons text-secondary">send</span> Send a
              Message
            </h2>

            {/* Status message */}
            {status.message && (
              <div
                className={`mb-6 rounded-lg p-4 text-sm font-semibold ${
                  status.type === "success"
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                    : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200"
                }`}
              >
                {status.message}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold opacity-70">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-zinc-800 border-none rounded-lg p-4 form-input-focus ring-1 ring-slate-200 dark:ring-zinc-700 focus:ring-2"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold opacity-70">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-zinc-800 border-none rounded-lg p-4 form-input-focus ring-1 ring-slate-200 dark:ring-zinc-700 focus:ring-2"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold opacity-70">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-zinc-800 border-none rounded-lg p-4 form-input-focus ring-1 ring-slate-200 dark:ring-zinc-700 focus:ring-2"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold opacity-70">
                  Subject
                </label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-zinc-800 border-none rounded-lg p-4 form-input-focus ring-1 ring-slate-200 dark:ring-zinc-700 focus:ring-2"
                  required
                >
                  <option>Table Reservation</option>
                  <option>Catering Inquiry</option>
                  <option>General Feedback</option>
                  <option>Work with Us</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold opacity-70">
                  Your Message
                </label>
                <textarea
                  rows={4}
                  placeholder="Tell us how we can help..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-zinc-800 border-none rounded-lg p-4 form-input-focus ring-1 ring-slate-200 dark:ring-zinc-700 focus:ring-2"
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-primary hover:bg-orange-600 text-white font-bold py-4 rounded-lg shadow-lg shadow-primary/20 transition-all transform hover:-translate-y-1 ${
                  isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>

          {/* Contact Information & Socials */}
          <div className="space-y-8">
            {/* Direct Details */}
            <div className="grid gap-6">
              <div className="flex items-start gap-5 p-6 rounded-xl bg-white dark:bg-zinc-900 shadow-sm border border-slate-100 dark:border-zinc-800">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="material-icons text-secondary">phone</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Call Us</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-2">
                    Speak directly with us.
                  </p>
                  <a
                    className="text-primary font-bold hover:underline"
                    href="tel:+447465682308"
                  >
                    07465 682308
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5 p-6 rounded-xl bg-white dark:bg-zinc-900 shadow-sm border border-slate-100 dark:border-zinc-800">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="material-icons text-secondary">email</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Email Us</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-2">
                    For general inquiries.
                  </p>
                  <a
                    className="text-primary font-bold hover:underline"
                    href="mailto:deedeliciouss@gmail.com"
                  >
                    deedeliciouss@gmail.com 
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5 p-6 rounded-xl bg-white dark:bg-zinc-900 shadow-sm border border-slate-100 dark:border-zinc-800">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="material-icons text-secondary">
                    location_on
                  </span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Visit Us</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-2">
                    The heart of authentic Caribbean flavor.
                  </p>
                  <p className="font-bold text-primary">
                    112 Breck Road, Anfield, Liverpool L4 2RD
                  </p>
                </div>
              </div>
            </div>

            {/* Social Bar */}
            <div className="p-8 rounded-xl bg-dark-green text-white">
              <h3 className="text-xl font-bold mb-6">Join the Community</h3>
              <div className="flex gap-4">
                {/* Instagram */}
                <a
                  className="w-14 h-14 bg-white/10 hover:bg-secondary rounded-full flex items-center justify-center transition-all group"
                  href="https://www.instagram.com/dee.delici0us/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    alt="Instagram"
                    className="w-6 h-6"
                    src="https://upload.wikimedia.org/wikipedia/commons/e/e7/Instagram_logo_2016.svg"
                  />
                </a>

                {/* TikTok */}
                <a
                  className="w-14 h-14 bg-white/10 hover:bg-secondary rounded-full flex items-center justify-center transition-all group"
                  href="https://www.tiktok.com/@deedeliciousresta?_r=1&_t=ZN-93wa7thxHfi"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    alt="TikTok"
                    className="w-6 h-6"
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/34/Ionicons_logo-tiktok.svg/960px-Ionicons_logo-tiktok.svg.png?20230423144016"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map & Hours Section */}
      <section className="bg-slate-100 dark:bg-zinc-950/50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Opening Hours */}
            <div className="lg:col-span-2 order-2 lg:order-1">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-extrabold text-dark-green dark:text-white">
                  Opening Hours
                </h2>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 text-500 font-semibold">
                  <span>Monday</span>
                  <span>Closed</span>
                </div>
                <div className="flex justify-between items-center py-3 text-500 font-semibold">
                  <span>Tuesday</span>
                  <span>Closed</span>
                </div>
                <div className="flex justify-between items-center py-3 text-500 font-semibold">
                  <span>Wednesday</span>
                  <span>12pm - 10pm</span>
                </div>
                <div className="flex justify-between items-center py-3 text-500 font-semibold">
                  <span>Thursday</span>
                  <span>12pm - 10pm</span>
                </div>
                <div className="flex justify-between items-center py-3 text-500 font-semibold">
                  <span>Friday</span>
                  <span>12pm - 10pm</span>
                </div>
                <div className="flex justify-between items-center py-3 text-500 font-semibold">
                  <span>Saturday</span>
                  <span>12pm - 11pm</span>
                </div>
                <div className="flex justify-between items-center py-3 text-500 font-semibold">
                  <span>Sunday</span>
                  <span>2pm - 9pm</span>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="lg:col-span-3 order-1 lg:order-2 h-[450px] lg:h-auto min-h-[400px] rounded-2xl overflow-hidden shadow-2xl border-4 border-white dark:border-zinc-800">
              <iframe
                title="Dee Delicious Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2377.4314034586464!2d-2.956703522946708!3d53.424994368835485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487b21715aa4dc89%3A0x9ac21217ac8bfe84!2s112%20Breck%20Rd%2C%20Anfield%2C%20Liverpool%20L4%202RD%2C%20UK!5e0!3m2!1sen!2sng!4v1770832984567!5m2!1sen!2sng"
                className="w-full h-full"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
